import { Helmet } from 'react-helmet-async';

import { PLACEHOLDER_ACTIVITY_DATE_ISO } from 'src/constants/activity';

import { SITE_URL, toPlainText, toAbsoluteUrl } from './page-seo';

// ----------------------------------------------------------------------

type Props = {
  activity: any;
};

const INVALID_DATES = [PLACEHOLDER_ACTIVITY_DATE_ISO, '1970-01-01T00:00:00.000Z'];

// Tarihler gün bazında tutuluyor (T00:00:00.000Z), saat dilimi kaymasın diye sadece günü kullan
function toSchemaDate(value?: string) {
  if (!value || INVALID_DATES.includes(value)) return null;
  return Number.isNaN(new Date(value).getTime()) ? null : value.slice(0, 10);
}

/**
 * Aktivite detay sayfası için schema.org Event structured data.
 * Tarihi geçmiş aktivitelerde de basılır (geçmiş startDate/endDate ile), sadece offers eklenmez.
 * Tarihi belli olmayan (placeholder) aktivitelerde startDate zorunlu olduğu için basılmaz.
 */
export function ActivityEventJsonLd({ activity }: Props) {
  const startDate = toSchemaDate(activity?.schedule?.startDate);

  if (!activity?._id || !startDate) return null;

  const endDate = toSchemaDate(activity?.schedule?.endDate) ?? startDate;
  const listing = activity?.listing;
  const isOnline = listing?.format === 'online';
  const url = `${SITE_URL}/aktivite/${listing?._id ?? activity._id}`;
  const images = Array.from(
    new Set(
      [listing?.coverImage, ...(listing?.images ?? [])]
        .filter(Boolean)
        .map((image: string) => toAbsoluteUrl(image))
    )
  );
  const location = activity?.location;
  const organizerName = listing?.exteriorOrganizer?.name || activity?.createdBy?.displayName;

  const data: Record<string, any> = {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: activity?.title || listing?.title,
    description: toPlainText(listing?.description || activity?.description, 500) || undefined,
    image: images.length ? images : undefined,
    url,
    startDate,
    endDate,
    // schema.org'da "sona erdi" durumu yok; geçmiş etkinlikler EventScheduled + geçmiş tarihle ifade edilir
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode: isOnline
      ? 'https://schema.org/OnlineEventAttendanceMode'
      : 'https://schema.org/OfflineEventAttendanceMode',
    location: isOnline
      ? { '@type': 'VirtualLocation', url }
      : {
          '@type': 'Place',
          name: location?.address?.title || location?.district || location?.city || undefined,
          address: {
            '@type': 'PostalAddress',
            streetAddress: location?.address?.description || undefined,
            addressLocality: location?.district || undefined,
            addressRegion: location?.city || undefined,
            addressCountry: 'TR',
          },
          ...(location?.lat && location?.long
            ? {
                geo: {
                  '@type': 'GeoCoordinates',
                  latitude: location.lat,
                  longitude: location.long,
                },
              }
            : {}),
        },
    organizer: organizerName ? { '@type': 'Organization', name: organizerName } : undefined,
  };

  if (!activity?.isPast && (activity?.fee === 'free' || activity?.price)) {
    data.offers = {
      '@type': 'Offer',
      url,
      price: activity?.fee === 'free' ? 0 : activity.price,
      priceCurrency: 'TRY',
      availability: 'https://schema.org/InStock',
    };
  }

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(data)}</script>
    </Helmet>
  );
}
